import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, inject } from '@angular/core';

export type TranslationData = Record<string, any>;

/**
 * TranslateService - Çoklu dil desteği için çeviri servisi
 * 
 * Bu servis uygulama genelinde kullanılan çeviri işlemlerini yönetir.
 * Dil dosyalarını yükler, çeviri metinlerini getirir ve dil değişikliklerini yönetir.
 */
@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  /**
   * HTTP client servisi
   */
  private http = inject(HttpClient);

  /**
   * Çeviri verileri
   */
  private translateData: TranslationData = {};

  /**
   * Mevcut dil kodu
   */
  private currentLanguage = 'en';

  /**
   * Varsayılan dil kodu
   */
  private defaultLanguage = 'en';

  /**
   * Dil değişikliği event'i
   */
  public languageChange = new EventEmitter<string>();

  /**
   * Çeviri yükleme event'i (library çevirileri yüklendiğinde emit edilir)
   */
  public translationsLoaded = new EventEmitter<void>();

  /**
   * Yüklenmiş kütüphane çeviri yolları (dil değiştiğinde yeniden yüklemek için)
   */
  private loadedLibraryPaths: string[] = [];

  /**
   * BroadcastChannel - Uygulamalar arası dil değişikliği bildirimi için
   */
  private broadcastChannel?: BroadcastChannel;

  /**
   * Kendi gönderdiğimiz mesajları ignore etmek için flag
   */
  private isInternalChange = false;

  /**
   * TranslateService constructor
   */
  constructor() {
    // LocalStorage'dan kayıtlı dili yükle
    const storedLang = this.getStoredLanguage();
    if (storedLang) {
      this.currentLanguage = storedLang;
    }

    // BroadcastChannel'ı başlat (tarayıcı desteği varsa)
    this.setupBroadcastChannel();
  }

  /**
   * BroadcastChannel'ı kurar ve mesaj dinleyicisini başlatır
   * Diğer uygulamalardan gelen dil değişikliklerini yakalar
   */
  private setupBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      // BroadcastChannel desteklenmiyorsa sessizce devam et
      return;
    }

    try {
      this.broadcastChannel = new BroadcastChannel('dental-ui-language-change');

      this.broadcastChannel.onmessage = (event: MessageEvent<{ language: string }>) => {
        // Kendi gönderdiğimiz mesajları ignore et
        if (this.isInternalChange) {
          return;
        }

        const newLanguage = event.data?.language?.toLowerCase();
        if (newLanguage && newLanguage !== this.currentLanguage) {
          // Diğer uygulamadan gelen dil değişikliğini yükle
          this.loadLanguageFromBroadcast(newLanguage);
        }
      };
    } catch (error) {
      console.warn('Failed to setup BroadcastChannel:', error);
    }
  }

  /**
   * BroadcastChannel'dan gelen dil değişikliğini yükler
   * Diğer uygulamalardan gelen mesajlardan tetiklenir
   * 
   * @param languageCode - Dil kodu
   */
  private loadLanguageFromBroadcast(languageCode: string): void {
    const normalizedLang = languageCode.toLowerCase();

    this.loadTranslations(normalizedLang)
      .then(() => {
        // Yüklenmiş kütüphane çevirilerini yeniden yükle
        const libraryPromises = this.loadedLibraryPaths.map(path => {
          const updatedPath = path.replace(/\/[a-z]{2}\.json$/, `/${normalizedLang}.json`);
          // Path'ten library name'i çıkar: /assets/i18n/patient/tr.json -> patient
          const libraryNameMatch = path.match(/\/assets\/i18n\/([^/]+)\//);
          const libraryName = libraryNameMatch ? libraryNameMatch[1] : undefined;
          return this.loadLibraryTranslations(updatedPath, normalizedLang, libraryName);
        });

        return Promise.all(libraryPromises);
      })
      .then(() => {
        this.currentLanguage = normalizedLang;
        // LocalStorage'a kaydet (senkronizasyon için)
        this.storeLanguage(normalizedLang);
        // Event'leri emit et
        this.languageChange.emit(normalizedLang);
        this.translationsLoaded.emit();
      })
      .catch((error) => {
        console.error(`Error loading language from broadcast: ${normalizedLang}`, error);
      });
  }

  /**
   * Çeviri metnini getirir
   * 
   * @param key - Çeviri anahtarı (nested key'ler desteklenir: "errors.emailRequired")
   * @param params - Parametreler ($1, $2, vb. ile değiştirilecek değerler)
   * @returns Çevrilmiş metin
   */
  getText(key: string, ...params: any[]): string {
    // Nested key desteği (örn: "errors.emailRequired")
    const keys = key.split('.');
    let translatedText: any = this.translateData;

    // Nested key'leri traverse et
    for (const k of keys) {
      if (translatedText && typeof translatedText === 'object') {
        translatedText = translatedText[k];
      } else {
        translatedText = undefined;
        break;
      }
    }

    // Eğer çeviri bulunamadıysa, key'i döndür
    if (typeof translatedText !== 'string') {
      translatedText = key;
    }

    // Parametreleri değiştir
    if (params.length === 1 && typeof params[0] === 'object' && params[0] !== null) {
      // Named parameters support: {{key}}
      const paramObj = params[0];
      Object.keys(paramObj).forEach(k => {
        // Use a global regex to replace all occurrences
        const regex = new RegExp(`{{${k}}}`, 'g');
        translatedText = translatedText.replace(regex, String(paramObj[k]));
      });
    } else {
      // Positional parameters support: $1, $2, vb.
      params.forEach((param, index) => {
        const placeholder = `$${index + 1}`;
        translatedText = translatedText.replace(placeholder, String(param));
      });
    }

    return translatedText;
  }

  /**
   * Mevcut dili getirir
   * 
   * @returns Dil kodu
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Dil ayarını yapar ve çeviri dosyasını yükler
   * Yüklenmiş tüm kütüphane çevirileri de yeniden yüklenir
   * 
   * @param languageCode - Dil kodu (en, tr, EN, TR vb. - otomatik normalize edilir)
   * @returns Promise
   */
  setLanguage(languageCode: string): Promise<void> {
    // Dil kodunu normalize et (küçük harfe çevir)
    const normalizedLang = languageCode.toLowerCase();

    return new Promise((resolve, reject) => {
      this.loadTranslations(normalizedLang)
        .then(() => {
          // Yüklenmiş kütüphane çevirilerini yeniden yükle
          const libraryPromises = this.loadedLibraryPaths.map(path => {
            // Path'teki dil kodunu güncelle
            const updatedPath = path.replace(/\/[a-z]{2}\.json$/, `/${normalizedLang}.json`);
            return this.loadLibraryTranslations(updatedPath, normalizedLang);
          });

          return Promise.all(libraryPromises);
        })
        .then(() => {
          this.currentLanguage = normalizedLang;
          this.storeLanguage(normalizedLang);
          this.languageChange.emit(normalizedLang);

          // Diğer uygulamalara dil değişikliğini bildir
          this.broadcastLanguageChange(normalizedLang);

          resolve();
        })
        .catch((error) => {
          console.error(`Error loading language: ${normalizedLang}`, error);
          // Hata durumunda varsayılan dile geri dön
          if (this.currentLanguage !== this.defaultLanguage) {
            this.setLanguage(this.defaultLanguage).then(resolve).catch(reject);
          } else {
            reject(error);
          }
        });
    });
  }

  /**
   * Çeviri dosyalarını yükler
   * 
   * @param language - Dil kodu
   * @returns Promise
   */
  private loadTranslations(language: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .get<TranslationData>(`/assets/i18n/${language}.json`)
        .subscribe({
          next: (data) => {
            this.translateData = { ...data };
            // Pipe'ları bilgilendir
            this.translationsLoaded.emit();
            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }

  /**
   * Belirli bir kütüphanenin çeviri dosyasını yükler ve mevcut çevirilere merge eder
   * Bu metod lazy load edilen kütüphaneler için kullanılır
   * 
   * @param translationPath - Çeviri dosyasının yolu (örn: '/assets/i18n/login/en.json')
   * @param language - Dil kodu (opsiyonel, mevcut dil kullanılır)
   * @returns Promise
   * 
   * @example
   * // Component'te kullanım
   * ngOnInit() {
   *   const translateService = inject(TranslateService);
   *   const lang = translateService.getCurrentLanguage();
   *   translateService.loadLibraryTranslations(`/assets/i18n/login/${lang}.json`);
   * }
   */
  loadLibraryTranslations(translationPath: string, language?: string, libraryName?: string): Promise<void> {
    // Path'i kaydet (dil değiştiğinde yeniden yüklemek için)
    if (!this.loadedLibraryPaths.includes(translationPath)) {
      this.loadedLibraryPaths.push(translationPath);
    }

    return new Promise((resolve, reject) => {
      this.http
        .get<TranslationData>(translationPath)
        .subscribe({
          next: (data) => {
            // Mevcut çevirilere merge et (library çevirileri önceliklidir)
            this.translateData = this.deepMerge(this.translateData, data);
            // Pipe'ları bilgilendir
            this.translationsLoaded.emit();
            resolve();
          },
          error: (error) => {
            // Hata durumunda sessizce devam et (library çevirileri opsiyonel)
            console.error(`[TranslateService] Failed to load library translations from ${translationPath}`, error);
            resolve(); // Reject yerine resolve - library çevirileri opsiyonel
          },
        });
    });
  }

  /**
   * Kütüphane çeviri dosyasını kolay yükleme için helper metod
   * Mevcut dili otomatik kullanır
   * 
   * @param libraryName - Kütüphane adı (örn: 'login', 'profile')
   * @returns Promise
   * 
   * @example
   * // Component'te kullanım
   * ngOnInit() {
   *   const translateService = inject(TranslateService);
   *   translateService.loadLibrary('login');
   * }
   */
  loadLibrary(libraryName: string): Promise<void> {
    const lang = this.getCurrentLanguage();
    const path = `/assets/i18n/${libraryName}/${lang}.json`;
    return this.loadLibraryTranslations(path, lang, libraryName);
  }

  /**
   * İki objeyi derinlemesine merge eder
   * 
   * @param target - Hedef obje
   * @param source - Kaynak obje (önceliklidir)
   * @returns Merge edilmiş obje
   */
  private deepMerge(target: any, source: any): any {
    const output = { ...target };

    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }

    return output;
  }

  /**
   * Bir değerin obje olup olmadığını kontrol eder
   */
  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
  /**
   * LocalStorage'a dil kodunu kaydeder
   * 
   * @param languageCode - Dil kodu
   */
  private storeLanguage(languageCode: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('dental-ui-language', languageCode);
    }
  }

  /**
   * LocalStorage'dan dil kodunu getirir
   * 
   * @returns Dil kodu veya null (normalize edilmiş - küçük harf)
   */
  private getStoredLanguage(): string | null {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('dental-ui-language');
      return stored ? stored.toLowerCase() : null;
    }
    return null;
  }

  /**
   * BroadcastChannel üzerinden dil değişikliğini diğer uygulamalara bildirir
   * 
   * @param languageCode - Dil kodu
   */
  private broadcastLanguageChange(languageCode: string): void {
    if (!this.broadcastChannel) {
      return;
    }

    try {
      // Kendi gönderdiğimiz mesajı işaretle
      this.isInternalChange = true;

      this.broadcastChannel.postMessage({ language: languageCode });

      // Flag'i sıfırla (setTimeout ile async olarak)
      setTimeout(() => {
        this.isInternalChange = false;
      }, 0);
    } catch (error) {
      console.warn('Failed to broadcast language change:', error);
      this.isInternalChange = false;
    }
  }

  /**
   * Uygulama başlangıcında çeviri dosyasını yükler.
   * `preloadLibraries` first paint'ten önce merge edilir; aksi halde
   * production gecikmesinde SETTINGS.TITLE gibi anahtarlar bir an görünür.
   */
  initialize(preloadLibraries: string[] = []): Promise<void> {
    const language = this.currentLanguage || this.defaultLanguage;
    return this.setLanguage(language).then(() =>
      Promise.all(preloadLibraries.map((libraryName) => this.loadLibrary(libraryName))).then(
        () => undefined
      )
    );
  }
}

